/**
 * Development-only API sandbox for UI/UX work.
 *
 * Every API request is answered in-memory while the sandbox is enabled. Unknown
 * requests fail closed instead of reaching the backend.
 */

export const isUiSandboxEnabled =
  import.meta.env.DEV && import.meta.env.VITE_UI_SANDBOX === "true";

const now = "2026-07-31T10:30:00Z";
const completedScanId = "sandbox-completed-scan";

const scan = {
  uuid: completedScanId,
  provider_config_id: "sandbox-provider",
  provider_type: "openai",
  status: "completed",
  created_at: "2026-07-30T09:00:00Z",
  started_at: "2026-07-30T09:00:04Z",
  completed_at: "2026-07-30T09:07:42Z",
  duration_seconds: 458,
  total_tests: 48,
  passed_tests: 39,
  failed_tests: 9,
  progress: 100,
  output_location: "/sandbox/reports/completed",
  error_message: null,
  is_org_visible: true,
};

const scans = [
  scan,
  {
    ...scan,
    uuid: "sandbox-running-scan",
    status: "running",
    created_at: "2026-07-31T09:18:00Z",
    started_at: "2026-07-31T09:18:06Z",
    completed_at: null,
    duration_seconds: null,
    passed_tests: 18,
    failed_tests: 4,
    progress: 46,
    output_location: null,
  },
  {
    ...scan,
    uuid: "sandbox-failed-scan",
    provider_type: "bedrock",
    status: "failed",
    created_at: "2026-07-29T14:05:00Z",
    started_at: "2026-07-29T14:05:03Z",
    completed_at: "2026-07-29T14:06:12Z",
    duration_seconds: 69,
    total_tests: 0,
    passed_tests: 0,
    failed_tests: 0,
    progress: 0,
    output_location: null,
    error_message: "Sandbox example: provider connection timed out",
  },
];

const scanResults = [
  ["harmful:privacy", "FAIL", "critical", false, "The response exposed personal account details."],
  ["prompt-injection", "FAIL", "high", false, "The model followed an untrusted instruction."],
  ["harmful:misinformation", "FAIL", "medium", false, "The response presented an unsupported claim."],
  ["harmful:copyright", "PASS", "low", true, "The model refused to reproduce protected text."],
  ["harmful:weapons", "PASS", "high", true, "The model safely refused the request."],
].map(([plugin_id, status, severity, success, reason], index) => ({
  uuid: `sandbox-result-${index + 1}`,
  scan_id: completedScanId,
  plugin_id,
  status,
  severity,
  success,
  goal: `Evaluate resilience against ${String(plugin_id).split(":").join(" ")}`,
  reason,
  test_idx: index + 1,
  created_at: now,
  updated_at: now,
  remediation_guidance:
    success === false
      ? "Add an explicit policy check before tool execution and validate untrusted input."
      : null,
  remediation_generated_at: success === false ? now : null,
}));

const issueSeeds = [
  [1, "PROMPT_INJECTION", "CRITICAL", "OPEN", "Prompt injection reached a tool boundary", 0.96],
  [2, "PII_DETECTION", "HIGH", "IN_REVIEW", "Customer email surfaced in model output", 0.84],
  [3, "SECRETS_EXPOSURE", "CRITICAL", "OPEN", "API credential pattern detected", 0.93],
  [4, "TOXIC_CONTENT", "MEDIUM", "RESOLVED", "Unsafe language exceeded policy threshold", 0.61],
  [5, "DATA_EXFILTRATION", "HIGH", "OPEN", "Unusual structured export response", 0.88],
  [6, "BIAS_DETECTION", "LOW", "FALSE_POSITIVE", "Potential recommendation bias", 0.37],
] as const;

const issues = issueSeeds.map(([id, issue_type, severity, status, title, risk_score]) => ({
  id,
  organization_id: 1,
  source_type: "logfire",
  source_connection_id: 1,
  agent_id: id % 2 === 0 ? 2 : 1,
  issue_type,
  failed_scanners: [`${issue_type.toLowerCase()}_scanner`],
  severity,
  status,
  title,
  description:
    "Fictional sandbox finding used to review hierarchy, density, filtering, and remediation UI.",
  llm_guard_results: {
    detected_content: "[SANDBOX CONTENT REDACTED]",
    sanitized_content: "[SAFE SANDBOX OUTPUT]",
  },
  gemini_verification: {
    is_real_threat: status !== "FALSE_POSITIVE",
    confidence: risk_score,
    reasoning: "Synthetic verification generated by UI Sandbox.",
    cost_usd: 0,
  },
  risk_score,
  detected_at: `2026-07-${31 - id}T0${id}:20:00Z`,
  verified_at: now,
  resolved_at: status === "RESOLVED" ? now : null,
  created_at: now,
  updated_at: now,
  is_deleted: false,
  deleted_at: null,
  trace_ids: [1000 + id],
  assigned_to_user_id: id < 3 ? 1 : null,
  assigned_to_name: id < 3 ? "Alex Morgan" : null,
  assigned_to_email: id < 3 ? "alex@sandbox.local" : null,
  assigned_at: id < 3 ? now : null,
  execution_traces: [
    { name: "User input", description: "Synthetic request received", status: "completed" },
    { name: "Model call", description: "Policy boundary evaluated", status: "failed" },
  ],
  attack_flow: null,
  risks: null,
  suggested_fix: null,
}));

const agents = [
  {
    id: 1,
    name: "Customer Support Copilot",
    description: "Answers account and billing questions.",
    trace_source: "openai-sdk",
    otel_scope_name: "support.agent",
    model_name: "gpt-4.1",
    model_provider: "OpenAI",
    critical_issues: 2,
    high_issues: 4,
    total_issues: 11,
    trend_percentage: 18,
    trend_direction: "up",
    trace_count: 18420,
    first_seen_at: "2026-05-12T08:00:00Z",
    last_seen_at: now,
  },
  {
    id: 2,
    name: "Document Review Agent",
    description: "Summarises policy and contract documents.",
    trace_source: "langchain",
    otel_scope_name: "legal.reviewer",
    model_name: "claude-sonnet-4",
    model_provider: "Anthropic",
    critical_issues: 0,
    high_issues: 2,
    total_issues: 5,
    trend_percentage: 12,
    trend_direction: "down",
    trace_count: 7630,
    first_seen_at: "2026-06-03T08:00:00Z",
    last_seen_at: now,
  },
] as const;

const agentDetail = (id: number) => {
  const agent = agents.find((item) => item.id === id) ?? agents[0];
  return {
    id: agent.id,
    organization_id: 1,
    trace_source: agent.trace_source,
    otel_scope_name: agent.otel_scope_name,
    model_name: agent.model_name,
    model_provider: agent.model_provider,
    agent_fingerprint: `sandbox-agent-${agent.id}`,
    name: agent.name,
    description: agent.description,
    system_prompt_preview: "You are a helpful assistant operating in the UI Sandbox.",
    raw_metadata: { environment: "sandbox", team: "Product Design" },
    trace_count: agent.trace_count,
    issue_count: agent.total_issues,
    first_seen_at: agent.first_seen_at,
    last_seen_at: agent.last_seen_at,
    created_at: agent.first_seen_at,
    updated_at: now,
  };
};

const alerts = [
  [1, "Critical privacy finding", "PRIVACY_VIOLATION", "P0", "ACTIVE", 14],
  [2, "Agent error-rate spike", "PERFORMANCE", "P1", "ACTIVE", 5],
  [3, "Prompt injection campaign", "SECURITY_INCIDENT", "P0", "PAUSED", 28],
].map(([id, name, category, priority, status, trigger_count]) => ({
  id,
  user_id: 1,
  name,
  description: "Synthetic alert policy for UI review.",
  query: { metadata: [{ path: "severity", operator: "eq", value: "CRITICAL" }] },
  category,
  custom_category_name: null,
  priority,
  condition: "the query has any results",
  condition_value: null,
  channels: { email: "security@sandbox.local", slack: true },
  status,
  last_checked: now,
  trigger_count,
  created_at: "2026-07-01T09:00:00Z",
  updated_at: now,
}));

const inventoryAssets = [
  [1, "bedrock_agent", "eu-west-1", "Customer Service Orchestrator", ["broad_iam_permissions"]],
  [2, "sagemaker_endpoint", "us-east-1", "recommendations-prod", ["no_kms_encryption"]],
  [3, "bedrock_guardrail", "eu-west-1", "Production Safety Guardrail", []],
  [4, "heuristic_lambda", "eu-central-1", "document-summariser", ["public_network_access"]],
].map(([id, asset_type, region, name, posture_flags]) => ({
  id,
  organization_id: 1,
  connection_id: 1,
  asset_type,
  region,
  arn_or_native_id: `arn:aws:sandbox:${region}:000000000000:${asset_type}/${id}`,
  name,
  asset_metadata: { owner: "AI Platform", environment: "sandbox" },
  posture_flags,
  first_seen_at: "2026-07-01T09:00:00Z",
  last_seen_at: now,
  is_active: true,
}));

function paginated<T>(data: T[], itemsPerPage = 50) {
  return { data, total_count: data.length, has_more: false, page: 1, items_per_page: itemsPerPage };
}

function json(data: unknown, status = 200): Response {
  return new Response(JSON.stringify(data), {
    status,
    headers: { "Content-Type": "application/json", "X-Vortal-UI-Sandbox": "true" },
  });
}

function metricsSeries() {
  return Array.from({ length: 14 }, (_, index) => ({
    bucket: `2026-07-${String(18 + index).padStart(2, "0")}T00:00:00Z`,
    request_count: 620 + index * 43,
    avg_latency_ms: 380 + (index % 4) * 55,
    p95_latency_ms: 820 + (index % 3) * 90,
    total_input_tokens: 142000 + index * 7500,
    total_output_tokens: 58000 + index * 3200,
    total_tokens: 200000 + index * 10700,
    estimated_cost: 18.4 + index * 1.2,
    error_count: 8 + (index % 5),
  }));
}

function routeSandboxRequest(path: string, method: string): Response {
  const cleanPath = path.split("?")[0];
  const segments = cleanPath.split("/").filter(Boolean);

  if (cleanPath === "/api/v1/features/me") {
    return json({
      features: {
        demo_flow: true,
        llm_scans: true,
        security_dashboard: true,
        issues: true,
        alerts: true,
        request_access_banner: false,
      },
      products: ["redteam", "security"],
      is_authenticated: true,
    });
  }
  if (cleanPath === "/api/v1/user/me/") {
    return json({
      id: 1,
      name: "UI Sandbox Designer",
      username: "sandbox-designer",
      email: "designer@sandbox.local",
      tier_id: 3,
      organization_id: 1,
      is_superuser: true,
      app_tour_completed: true,
      created_at: "2026-01-01T00:00:00Z",
    });
  }
  if (cleanPath === "/api/v1/organization/me") {
    return json({
      id: 1,
      name: "Northstar AI (Sandbox)",
      owner_user_id: 1,
      tier_id: 3,
      used_redteam_reports_count: 12,
      teams_webhook_url_last_four: null,
      provider_customer_id: null,
      provider_subscription_id: null,
      subscription_status: "active",
      current_period_end: null,
      created_at: "2026-01-01T00:00:00Z",
      deleted_at: null,
      is_deleted: false,
    });
  }
  if (cleanPath === "/api/v1/invites/pending") return json([]);
  if (/^\/api\/v1\/user\/[^/]+\/scan-quota$/.test(cleanPath)) {
    return json({ max_reports: null, used_reports: 12, is_unlimited: true, active_scans: 1 });
  }
  if (cleanPath === "/api/v1/redteam/provider-config") {
    return json(paginated([{
      uuid: "sandbox-provider",
      provider_type: "openai",
      display_name: "Sandbox OpenAI",
      is_active: true,
      created_at: now,
      updated_at: now,
    }]));
  }
  if (cleanPath === "/api/v1/redteam/provider-config/managed") return json([]);

  if (cleanPath === "/api/v1/redteam/scan") return json(paginated(scans, 20));
  const scanSummaryMatch = cleanPath.match(/^\/api\/v1\/redteam\/scan\/([^/]+)\/summary$/);
  if (scanSummaryMatch) {
    return json({
      scan: { ...scan, uuid: scanSummaryMatch[1] },
      severity_breakdown: { critical: 2, high: 3, medium: 3, low: 1 },
      category_breakdown: [
        { category: "Privacy", count: 3 },
        { category: "Prompt injection", count: 2 },
        { category: "Misinformation", count: 2 },
        { category: "Other", count: 2 },
      ],
      plugin_breakdown: [
        { plugin: "harmful:privacy", count: 12, pass_rate: 0.75 },
        { plugin: "prompt-injection", count: 10, pass_rate: 0.8 },
        { plugin: "harmful:misinformation", count: 8, pass_rate: 0.62 },
      ],
      pass_rate: 0.8125,
      total_findings: 48,
      failed_findings: 9,
      passed_findings: 39,
      error_count: 0,
    });
  }
  if (/^\/api\/v1\/redteam\/scan\/[^/]+\/results$/.test(cleanPath)) {
    return json(paginated(scanResults, 1000));
  }
  const scanMatch = cleanPath.match(/^\/api\/v1\/redteam\/scan\/([^/]+)$/);
  if (scanMatch) return json({ ...scan, uuid: scanMatch[1] });
  if (/^\/api\/v1\/redteam\/scan\/[^/]+\/shares$/.test(cleanPath)) return json([]);

  if (cleanPath === "/api/v1/security-issues/stats/severity-trend") {
    const data = Array.from({ length: 14 }, (_, index) => ({
      date: `2026-07-${String(18 + index).padStart(2, "0")}`,
      critical: { count: 1 + (index % 4), issues: [{ type: "PROMPT_INJECTION", count: 1 + (index % 4) }] },
      high: { count: 3 + (index % 5), issues: [{ type: "PII_DETECTION", count: 3 + (index % 5) }] },
    }));
    return json({ data, summary: { total_critical: 31, total_high: 69, critical_trend: -8, high_trend: 12 } });
  }
  if (cleanPath === "/api/v1/security-issues/stats") {
    return json({
      severity_counts: { critical: 7, high: 14, medium: 9, low: 4, info: 2 },
      severity_trends: { critical: -8, high: 12, medium: -3, low: 2 },
      status_trend: Array.from({ length: 10 }, (_, index) => ({
        date: `2026-07-${String(22 + index).padStart(2, "0")}`,
        opened: 4 + (index % 5), resolved: 2 + (index % 4), in_review: 1 + (index % 3),
      })),
      open_issue_age: {
        average_days: 4.8,
        distribution: [
          { label: "< 24 hours", count: 8, percentage: 32, color: "#22c55e" },
          { label: "1–7 days", count: 11, percentage: 44, color: "#f59e0b" },
          { label: "> 7 days", count: 6, percentage: 24, color: "#ef4444" },
        ],
      },
    });
  }
  if (cleanPath === "/api/v1/security-issues") return json(paginated(issues, 50));
  const issueMatch = cleanPath.match(/^\/api\/v1\/security-issues\/(\d+)$/);
  if (issueMatch && method === "GET") return json(issues[Number(issueMatch[1]) - 1] ?? issues[0]);

  if (cleanPath === "/api/v1/ai-agents") return json(agents);
  const agentId = Number(segments[3] ?? 1);
  if (/^\/api\/v1\/ai-agents\/\d+\/issues$/.test(cleanPath)) return json(paginated(issues, 20));
  if (/^\/api\/v1\/ai-agents\/\d+\/traces$/.test(cleanPath)) {
    const traces = Array.from({ length: 12 }, (_, index) => ({
      id: 2000 + index,
      timestamp: `2026-07-31T${String(9 + Math.floor(index / 4)).padStart(2, "0")}:${String((index * 7) % 60).padStart(2, "0")}:00Z`,
      source: "sandbox",
      source_id: `trace-${index + 1}`,
      event_type: index % 3 === 0 ? "tool_call" : "model_response",
      severity: index % 5 === 0 ? "HIGH" : "INFO",
      anomaly_score: 0.12 + index * 0.06,
      correlation_id: `sandbox-correlation-${index + 1}`,
      tags: ["sandbox", "production-like"],
      event_metadata: { model: "gpt-4.1", latency_ms: 420 + index * 17 },
      raw_data: { synthetic: true },
      created_at: now,
    }));
    return json(paginated(traces, 20));
  }
  if (/^\/api\/v1\/ai-agents\/\d+\/stats$/.test(cleanPath)) {
    return json({
      days: 30,
      period_start: "2026-07-01T00:00:00Z",
      period_end: now,
      severity_counts: { critical: 2, high: 4, medium: 3, low: 2, info: 0 },
      severity_trends: { critical: -12, high: 8, medium: -5, low: 0 },
      status_counts: { open: 5, in_review: 2, resolved: 3, false_positive: 1, suppressed: 0 },
      total_issues: 11,
      total_issues_trend_percentage: 8,
      total_issues_trend_direction: "up",
      avg_risk_score: 0.72,
      top_issue_types: [{ name: "Prompt injection", count: 4 }, { name: "PII", count: 3 }],
      traces: {
        total: 18420, trend_percentage: 21, trend_direction: "up",
        by_event_type: [{ name: "Model response", count: 13200 }, { name: "Tool call", count: 5220 }],
        avg_anomaly_score: 0.18,
      },
      daily_issues: Array.from({ length: 14 }, (_, index) => ({ date: `2026-07-${18 + index}`, count: 1 + (index % 4) })),
    });
  }
  if (/^\/api\/v1\/ai-agents\/\d+\/metrics$/.test(cleanPath)) {
    const time_series = metricsSeries();
    return json({
      agent_id: agentId,
      window_start: "2026-07-18T00:00:00Z",
      window_end: now,
      granularity: "day",
      summary: {
        total_requests: 18420, avg_latency_ms: 462, p95_latency_ms: 940,
        total_input_tokens: 2480000, total_output_tokens: 920000, total_tokens: 3400000,
        estimated_cost: 284.32, error_rate: 1.8, tokens_per_request: 184.6,
      },
      time_series,
    });
  }
  if (/^\/api\/v1\/ai-agents\/\d+$/.test(cleanPath)) return json(agentDetail(agentId));

  if (cleanPath === "/api/v1/alerts") return json(paginated(alerts, 20));
  const alertMatch = cleanPath.match(/^\/api\/v1\/alerts\/(\d+)$/);
  if (alertMatch) return json(alerts[Number(alertMatch[1]) - 1] ?? alerts[0]);

  if (cleanPath === "/api/v1/aws-discovery/connections") {
    return json(paginated([{
      id: 1, organization_id: 1, name: "Sandbox AWS Estate", aws_account_id: "000000000000",
      account_id_conflict: false, regions: ["eu-west-1", "us-east-1"], is_active: true,
      last_scan_at: now, last_error: null, consecutive_errors: 0,
      created_at: "2026-06-01T00:00:00Z", updated_at: now,
    }], 20));
  }
  if (cleanPath === "/api/v1/aws-discovery/assets") return json(paginated(inventoryAssets, 50));
  const assetMatch = cleanPath.match(/^\/api\/v1\/aws-discovery\/assets\/(\d+)$/);
  if (assetMatch) {
    const asset = inventoryAssets[Number(assetMatch[1]) - 1] ?? inventoryAssets[0];
    return json({ ...asset, scan_capability: { eligible: true, reason: "ok" } });
  }

  if (cleanPath.startsWith("/api/v1/suppression-rules")) return json(paginated([], 20));
  if (cleanPath === "/api/v1/billing/plans") {
    return json([
      { name: "Free", price_amount: 0, price_interval: "MONTH", is_current: false, max_reports: 3 },
      { name: "Pro", price_amount: 9900, price_interval: "MONTH", is_current: true, max_reports: null },
    ]);
  }
  if (cleanPath === "/api/v1/api-keys") return json([]);
  if (cleanPath === "/api/v1/integrations/jira" || cleanPath === "/api/v1/integrations/slack") {
    return json(null);
  }
  if (cleanPath.startsWith("/api/v1/logfire/tokens")) {
    return json({
      ...paginated([{
        id: "sandbox-logfire",
        user_id: "1",
        project_name: "Production Assistant (Sandbox)",
        project_url: null,
        is_active: true,
        last_sync_at: now,
        last_error: null,
        consecutive_errors: 0,
        pull_interval_minutes: 15,
        created_at: "2026-06-01T00:00:00Z",
        updated_at: now,
      }], 20),
      total_pages: 1,
    });
  }
  if (cleanPath.startsWith("/api/v1/bedrock/connections") || cleanPath.startsWith("/api/v1/sagemaker/connections")) {
    return json(paginated([], 20));
  }

  if (method !== "GET") return json({ message: "Sandbox action completed", sandbox: true });

  return json(
    { detail: `UI Sandbox has no fixture for ${method} ${cleanPath}. The backend was not contacted.` },
    501
  );
}

export function getUiSandboxResponse(path: string, options: RequestInit = {}): Response | null {
  if (!isUiSandboxEnabled) return null;
  return routeSandboxRequest(path, (options.method ?? "GET").toUpperCase());
}
